<template>
  <div id="app">
    <Header />
    <transition
      name="fade"
      mode="out-in"
      @beforeLeave="beforeLeave"
      @enter="enter"
      @afterEnter="afterEnter"
    >
      <router-view />
    </transition>
    <FAQModal :show="showFAQModal"></FAQModal>
    <TrackingConsentPopup :eventBus="eventBus" />
    <SessionWarningModal
      :show="showSessionWarningModal"
      :showLogout="false"
      warningText="Please click continue session or your progress will be lost."
    ></SessionWarningModal>
  </div>
</template>

<script>
import Header from "@Shared/components/Header";
import TrackingConsentPopup from "@Shared/components/TrackingConsentPopup";
import { EventBus } from "@Shared/utils/event-bus";
import { mapGetters } from "vuex";
import { mapActions } from "vuex";
import Cookies from "js-cookie";
import FAQModal from "@Shared/components/FAQModal";
import SessionWarningModal from "@Shared/components/SessionWarningModal";

export default {
  name: "App",
  data() {
    return {
      eventBus: EventBus,
      showFAQModal: false,
    };
  },
  components: {
    FAQModal,
    Header,
    TrackingConsentPopup,
    SessionWarningModal,
  },
  computed: {
    ...mapGetters(["featureFlags", "showSessionWarningModal"]),
  },
  methods: {
    ...mapActions(["rerouteToVerifyPage"]),
    loadGoogleAnalytics() {
      if (!this.featureFlags) return;
      if (this.featureFlags.googleAnalytics.isEnabled === true) {
        this.$ga.start();
      }
    },
    enableDisableGoogleAnalytics(type) {
      if (type === "accept") {
        setTimeout(() => {
          // delay so that the script can load and the constructor doesn't overwrite this value
          this.$ga.enable();
          this.$ga.page(this.$router); // fire so we can capture the page they currently are on
        }, 500);
        this.$gtm.start();
      } else {
        this.$ga.disable();
      }
    },
    emitResize(event) {
      EventBus.$emit("window-resize", {
        width: event.currentTarget.innerWidth,
        height: event.currentTarget.innerHeight,
      });
    },
    getCookieStatus() {
      if (this.supportsLocalStorage) {
        return JSON.parse(localStorage.getItem("cb-tracking-consent"));
      } else {
        return Cookies.get("cb-tracking-consent");
      }
    },
    showFAQs() {
      this.showFAQModal = true;
    },
    hideFAQs() {
      this.showFAQModal = false;
    },
    showSessionWarning() {
      this.showSessionWarningModal = true;
    },
    hideSessionWarning() {
      this.showSessionWarningModal = false;
    },
    beforeLeave(element) {
      EventBus.$on("sessionWarningModalClose", this.hideSessionWarning);
      this.prevHeight = getComputedStyle(element).height;
    },
    enter(element) {
      const { height } = getComputedStyle(element);
      element.style.height = this.prevHeight;
      EventBus.$on("sessionWarningModalClose", this.hideSessionWarning);
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      EventBus.$on("sessionWarningModalClose", this.hideSessionWarning);
      element.style.height = "auto";
    },
  },
  watch: {
    featureFlags: {
      handler() {
        this.loadGoogleAnalytics();
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    EventBus.$on("tracking", this.enableDisableGoogleAnalytics);
    EventBus.$on("showFAQs", this.showFAQs);
    EventBus.$on("faqModalClose", this.hideFAQs);
  },
  mounted() {
    window.addEventListener("resize", this.emitResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.emitResize);
    EventBus.$off("tracking", this.enableDisableGoogleAnalytics);
    EventBus.$off("showFAQs", this.showFAQs);
    EventBus.$off("faqModalClose", this.hideFAQs);
  },
};
</script>

<style lang="scss">
html {
  height: 100%;
}

body {
  height: 100%;
  padding: 0;
  margin: 0;
  background-color: #ffffff !important;
  color: $color_gray-2;
}

#app {
  /*margin-top: 100px;*/
  min-height: 100%;
  position: relative;
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
